/// <amd-module name="Build/Admin/Pool/Components/AzureSubscriptionSettingsPanel" />
import * as React from "react";

import { IButtonProps } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Panel } from "VSSUI/Panel";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";

import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { IProjectData } from "Tfs/Platform/Page";
import { ITeamProject } from "TFS/Services/TfsProjectService";

import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";
import { ProjectPicker } from "Build/Admin/Common/Components/ProjectPicker";
import { VMScaleSetSettings } from "Build/Admin/Common/Components/VMScaleSetSettings";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";
import { ReimageDisabledWarning, reimageIsNotAvaialable } from "Build/Admin/Common/Components/ReimageDisabledWarning";

import * as Resources from "Build/Admin/Pool/Resources";

export interface IAzureSubscriptionSettingsPanelProps {
    actionCreator: PoolActionCreator;
    storesHub: StoresHub;
    isOpen: boolean;
    onDismiss(): void;
    recycleAfterEachUse?: boolean;
}

export interface IAzureSubscriptionSettingsPanelState {
    projectId?: string;
    serviceEndpoint: ServiceEndpoint | undefined;
    virtualMachineScaleSet: VirtualMachineScaleSet | undefined;
    disableSaveButton: boolean;
}

export class AzureSubscriptionSettingsPanel extends VssComponent<IAzureSubscriptionSettingsPanelProps, IAzureSubscriptionSettingsPanelState> {
    private _project: IProjectData | undefined;

    constructor(props: IAzureSubscriptionSettingsPanelProps, context: IVssComponentContext) {
        super(props, context);

        const projectDataService: IProjectDataService = context.pageContext.getService<IProjectDataService>("IProjectDataService");
        this._project = projectDataService.getProjectData();

        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.storesHub.selectionStore.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.selectionStore.removeChangedListener(this._onStoreChanged);
    }

    public render(): JSX.Element | null {
        return (
            <ConditionalChildren renderChildren={this.props.isOpen}>
                <Panel
                    className="pipelines-azure-subscription-settings-panel"
                    titleProps={{ text: Resources.AzureSubscriptionSettings }}
                    description={Resources.AzureSubscriptionSettingsDescription}
                    onDismiss={() => {
                        this._onStoreChanged();
                        this.props.onDismiss();
                    }}
                    footerButtonProps={this._getFooterButtonProps()}>
                    <div className="padding-top-8 flex-column flex-grow">
                        <ConditionalChildren renderChildren={this._project === undefined}>
                            <div className="padding-bottom-8">
                                {Resources.SelectProjectHeader}
                            </div>
                            <ProjectPicker projects={this.props.storesHub.projectStore.state} onProjectSelected={this._onProjectSelected} />
                        </ConditionalChildren>
                        <VMScaleSetSettings
                            actionCreator={this.props.actionCreator.serviceEndpointActionCreator}
                            serviceEndpointStore={this.props.storesHub.serviceEndpointStore}
                            virtualMachineScaleSetStore={this.props.storesHub.virtualMachineScaleSetStore}
                            projectId={this.state.projectId}
                            onSettingsChanged={this._onVMScaleSetSettingsChanged} />
                        <ReimageDisabledWarning
                            isVisible={!!this.state.virtualMachineScaleSet && this._restrictReimage(this.state.virtualMachineScaleSet)}
                        />
                    </div>
                </Panel>
            </ConditionalChildren>
        );
    }

    private _getFooterButtonProps = (): IButtonProps[] => {
        return [
            {
                text: Resources.Save,
                primary: true,
                onClick: this._saveAzureSubscriptionSettings,
                disabled: this.state.disableSaveButton
            }
        ];
    }

    private _onProjectSelected = (project: ITeamProject | undefined): void => {
        this.setState({
            projectId: project ? project.id : undefined
        });
    }

    private _onVMScaleSetSettingsChanged = (serviceEndpoint: ServiceEndpoint | undefined, virtualMachineScaleSet: VirtualMachineScaleSet | undefined): void => {
        this.setState({
            serviceEndpoint,
            virtualMachineScaleSet,
            disableSaveButton: this._shouldDisableSubmitButton({
                ...this.state,
                serviceEndpoint,
                virtualMachineScaleSet
            })
        });
    }

    private _saveAzureSubscriptionSettings = (): void => {
        const selectedServiceEndpoint: ServiceEndpoint | undefined = this.props.storesHub.selectionStore.state.selectedServiceEndpoint;
        const selectedVirtualMachineScaleSet: VirtualMachineScaleSet | undefined = this.props.storesHub.selectionStore.state.selectedVirtualMachineScaleSet;

        if (this.state.projectId &&
            this.state.serviceEndpoint &&
            this.state.virtualMachineScaleSet &&
            (this.state.serviceEndpoint.id !== selectedServiceEndpoint?.id || this.state.virtualMachineScaleSet.id !== selectedVirtualMachineScaleSet?.id)) {
            this.props.actionCreator.updateElasticPoolAzureSubscriptionSettings(this.state.projectId, this.state.serviceEndpoint, this.state.virtualMachineScaleSet);
            this.props.onDismiss();
        }
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores = (): IAzureSubscriptionSettingsPanelState => {
        return {
            projectId: this._project ? this._project.id : undefined,
            serviceEndpoint: undefined,
            virtualMachineScaleSet: undefined,
            disableSaveButton: true
        };
    }

    private _restrictReimage = (vmss?: VirtualMachineScaleSet): boolean => {
        return reimageIsNotAvaialable(vmss) && !!this.props.recycleAfterEachUse
    }

    private _shouldDisableSubmitButton = (newState: IAzureSubscriptionSettingsPanelState): boolean => {
        return !newState.serviceEndpoint ||
            !newState.virtualMachineScaleSet ||
            (newState.serviceEndpoint.id === this.props.storesHub.selectionStore.state.selectedServiceEndpoint?.id &&
                newState.virtualMachineScaleSet.id === this.props.storesHub.selectionStore.state.selectedVirtualMachineScaleSet?.id) ||
            this._restrictReimage(newState.virtualMachineScaleSet);
    }
}
